import { Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import ProtectedRoute from "./components/common/ProtectedRoute";
import RoleRoute from "./components/common/RoleRoute";
import GuestRoute from "./GuestRoute";
import { useThemeInitialization } from "./hooks/useThemeInitialization";
import { errorBoundaryUtils } from "./utils/errorHandler";

import AppLayout from "./components/layout/AppLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import Login from "./pages/Login";
import PlaceholderPage from "./pages/PlaceholderPage";
import HistoryLogsPage from "./pages/call-history-log/CallHistorylog";
import AllAppointmentsPage from "./pages/appointments/all-appointment/AllAppointment";
import AppointmentCalendarPage from "./pages/appointments/appointment-calendar/AppointmentCalendar";
import AddAgentPage from "./pages/agents/AddAgentPage";
import ManageAgentsPage from "./pages/agents/ManageAgentsPage";
import AddCampaignPage from "./pages/campaigns/AddCampaignPage";
import ManageCampaignsPage from "./pages/campaigns/ManageCampaignsPage";
import MyCampaignsPage from "./pages/campaigns/MyCampaignsPage";

import FallBack from "./components/common/FallBack";

import { useAuth } from "./hooks/useAuth";
import { Role } from "./types/roles";

import "./styles/index.css";
import ProfilePage from "./pages/settings/profile/Profile";

errorBoundaryUtils.setContext({
  app: "CRM Frontend",
  version: "1.0.0",
  environment: process.env.NODE_ENV,
});

function App() {
  useThemeInitialization();

  const { user } = useAuth();
  const isAdmin = user?.role === Role.ADMIN;
  const isSalesAgent = user?.role === Role.SALES_AGENT;
  const isCallingAgent = user?.role === Role.CALLING_AGENT;

  // Determine the default redirect path based on role
  const defaultRedirect = isAdmin
    ? "/admin/dashboard"
    : isSalesAgent
      ? "/all-appointments"
      : isCallingAgent
        ? "/auto-dialer"
        : "/login";

  return (
    <ErrorBoundary showDetails={process.env.NODE_ENV === "development"}>
      <div className="App">
        <Routes>
          {/* Guest-only Auth Pages */}
          <Route
            path="/login"
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Main App Layout - Protected */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout showSidebar={true} />
              </ProtectedRoute>
            }
          >
            {/* Default redirect by role */}
            <Route index element={<Navigate to={defaultRedirect} replace />} />

            {/* Admin Dashboard */}
            <Route
              path="admin/dashboard"
              element={
                <RoleRoute allowedRoles={[Role.ADMIN]}>
                  <AdminDashboard />
                </RoleRoute>
              }
            />

            {/* Calling Agent — My Campaigns */}
            <Route
              path="my-campaigns"
              element={
                <RoleRoute allowedRoles={[Role.CALLING_AGENT]}>
                  <MyCampaignsPage />
                </RoleRoute>
              }
            />

            {/* Profile — all roles */}
            <Route path="profile" element={<ProfilePage />} />

            {/* AI-Agent — Admin only */}
            <Route
              path="add-ai-agent"
              element={
                <RoleRoute allowedRoles={[Role.ADMIN]}>
                  <AddAgentPage />
                </RoleRoute>
              }
            />
            <Route
              path="ai-agents"
              element={
                <RoleRoute allowedRoles={[Role.ADMIN]}>
                  <ManageAgentsPage />
                </RoleRoute>
              }
            />
            <Route
              path="add-agent"
              element={<Navigate to="/add-ai-agent" replace />}
            />
            <Route
              path="all-users"
              element={<Navigate to="/ai-agents" replace />}
            />
            <Route
              path="all-agents"
              element={<Navigate to="/ai-agents" replace />}
            />

            {/* Users — Admin only */}
            <Route
              path="users"
              element={
                <RoleRoute allowedRoles={[Role.ADMIN]}>
                  <PlaceholderPage />
                </RoleRoute>
              }
            />

            {/* Campaigns — Admin only */}
            <Route
              path="add-campaign"
              element={
                <RoleRoute allowedRoles={[Role.ADMIN]}>
                  <AddCampaignPage />
                </RoleRoute>
              }
            />
            <Route
              path="campaigns"
              element={
                <RoleRoute allowedRoles={[Role.ADMIN]}>
                  <ManageCampaignsPage />
                </RoleRoute>
              }
            />

            {/* History Logs — all roles */}
            <Route path="auto-dialer" element={<HistoryLogsPage />} />

            {/* Appointments — all roles */}
            <Route path="all-appointments" element={<AllAppointmentsPage />} />
            <Route
              path="all-tasks"
              element={<Navigate to="/all-appointments" replace />}
            />
            <Route path="calendar" element={<AppointmentCalendarPage />} />

          </Route>

          {/* Legacy redirects */}
          <Route path="app/*" element={<Navigate to="/" replace />} />
          {/* 404 fallback */}
          <Route path="*" element={<FallBack />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}

export default App;
